import { getBmfCurves } from "../../libs/cognito-api/bmf-curves/bmf-curves";
import { getCurrencySpotQuote } from "../../libs/cognito-api/currency-spot-quote/currency-spot-quote";
import { getDashboard } from "../../libs/cognito-api/dashboard/dashboard";
import { getPtax } from "../../libs/cognito-api/ptax/ptax";
import { normalizeDate } from "../../shared/calendar";

const ptaxApi = getPtax();
const spotApi = getCurrencySpotQuote();
const dashboardApi = getDashboard();
const bmfCurvesApi = getBmfCurves();

type AnyRecord = Record<string, any>;

export async function ptaxD1(moeda: string, dataReferencia?: string | number, campo?: string): Promise<number> {
  const response = await ptaxApi.getPtaxQuotationsPreviousDaysPtaxLessHolidayCurrencyCode(
    normalizeCurrencyCode(moeda),
    { daysBack: 1, referenceDate: emptyDateToUndefined(dataReferencia) }
  );
  const data = unwrapData<AnyRecord>(response, "PTAX D-1 nao retornou dados.");
  const selectedField = normalizeOptionalField(campo, "venda");

  switch (normalizeKey(selectedField)) {
    case "compra":
    case "purchaserate":
      return requireNumber(data.purchaseRate, "PTAX compra nao retornada.");
    case "venda":
    case "salerate":
      return requireNumber(data.saleRate, "PTAX venda nao retornada.");
    case "paridadecompra":
      return requireNumber(data.purchaseParity, "Paridade de compra nao retornada.");
    case "paridadevenda":
      return requireNumber(data.saleParity, "Paridade de venda nao retornada.");
    default:
      throw new Error("Campo invalido. Use venda, compra, paridadeCompra ou paridadeVenda.");
  }
}

export async function ptax(moeda: string, data: string | number, campo?: string): Promise<number> {
  const quoteDate = normalizeDate(data);
  const response = await ptaxApi.getPtaxQuotationsPreviousDaysPtaxLessHolidayCurrencyCode(
    normalizeCurrencyCode(moeda),
    { daysBack: 1, referenceDate: nextIsoDate(quoteDate) }
  );
  const dataPoint = unwrapData<AnyRecord>(response, "PTAX da data nao retornou dados.");
  if (normalizeDate(String(dataPoint.quoteDate ?? "")) !== quoteDate) {
    throw new Error(`PTAX nao encontrada para ${quoteDate}.`);
  }
  const selectedField = normalizeOptionalField(campo, "venda");

  switch (normalizeKey(selectedField)) {
    case "compra":
    case "purchaserate":
      return requireNumber(dataPoint.purchaseRate, "PTAX compra nao retornada.");
    case "venda":
    case "salerate":
      return requireNumber(dataPoint.saleRate, "PTAX venda nao retornada.");
    case "paridadecompra":
      return requireNumber(dataPoint.purchaseParity, "Paridade de compra nao retornada.");
    case "paridadevenda":
      return requireNumber(dataPoint.saleParity, "Paridade de venda nao retornada.");
    default:
      throw new Error("Campo invalido. Use venda, compra, paridadeCompra ou paridadeVenda.");
  }
}

export async function spotFx(moedaOuPar: string, campo?: string): Promise<number> {
  const pair = normalizeCurrencyPair(moedaOuPar);
  const response = await spotApi.getCurrencySpotQuotesLatest({ pairs: [pair] });
  const data = unwrapData<any[]>(response, "Cambio spot nao retornou dados.");
  const quote = data.find((item) => normalizeCurrencyPair(item.pair ?? item.currencyPair ?? pair) === pair) ?? data[0];
  const latest = quote?.latestDaily ?? quote;
  const selectedField = normalizeOptionalField(campo, "bid");

  switch (normalizeKey(selectedField)) {
    case "bid":
    case "compra":
      return requireNumber(latest?.bid ?? latest?.currentQuote, "Spot bid nao retornado.");
    case "ask":
    case "venda":
      return requireNumber(latest?.ask, "Spot ask nao retornado.");
    case "high":
    case "maxima":
      return requireNumber(latest?.high, "Spot maxima nao retornado.");
    case "low":
    case "minima":
      return requireNumber(latest?.low, "Spot minima nao retornado.");
    default:
      throw new Error("Campo invalido. Use bid, ask, maxima ou minima.");
  }
}

export async function cdi(campo?: string): Promise<number> {
  const selectedField = normalizeOptionalField(campo, "anualizado");
  if (normalizeKey(selectedField) === "diario") {
    const response = await dashboardApi.getDashboardIndexesCdiDailyCurrent();
    const data = unwrapData<AnyRecord>(response, "CDI diario nao retornou dados.");
    return requireNumber(data.value, "CDI diario nao retornado.");
  }

  const response = await dashboardApi.getDashboardIndexesCdiDailyAnnualizedCurrent();
  const data = unwrapData<AnyRecord>(response, "CDI anualizado nao retornou dados.");

  switch (normalizeKey(selectedField)) {
    case "anualizado":
    case "anual":
      return requireNumber(data.annualized ?? data.value, "CDI anualizado nao retornado.");
    case "valor":
      return requireNumber(data.value, "CDI valor nao retornado.");
    default:
      throw new Error("Campo invalido. Use anualizado, diario ou valor.");
  }
}

export async function ipca(campo?: string): Promise<number> {
  const response = await dashboardApi.getDashboardIndexesIpcaMonthCurrent();
  const data = unwrapData<AnyRecord>(response, "IPCA nao retornou dados.");
  const selectedField = normalizeOptionalField(campo, "mensal");

  switch (normalizeKey(selectedField)) {
    case "mensal":
    case "valor":
      return requireNumber(data.value, "IPCA mensal nao retornado.");
    case "ano":
    case "acumuladoano":
      return requireNumber(data.accumulatedYear, "IPCA acumulado no ano nao retornado.");
    case "12m":
    case "acumulado12m":
      return requireNumber(data.accumulatedLast12Months, "IPCA acumulado 12m nao retornado.");
    default:
      throw new Error("Campo invalido. Use mensal, ano ou 12m.");
  }
}

export async function cdiImplicito(dataVencimento: string | number, campo?: string): Promise<number> {
  const response = await dashboardApi.getDashboardIndexesDi1CurveInterpolatedRate({ targetDate: normalizeDate(dataVencimento) });
  return selectInterpolatedRate(unwrapData<AnyRecord>(response, "CDI implicito nao retornou dados."), normalizeOptionalField(campo, "taxaAnual"));
}

export async function ipcaImplicito(dataVencimento: string | number, campo?: string): Promise<number> {
  const response = await dashboardApi.getDashboardIndexesImplicitIpcaCurveInterpolatedRate({ targetDate: normalizeDate(dataVencimento) });
  return selectInterpolatedRate(unwrapData<AnyRecord>(response, "IPCA implicito nao retornou dados."), normalizeOptionalField(campo, "taxaAnual"));
}

export async function forwardCurve(curva: string, dataInicial: string | number, dataFinal: string | number, campo?: string): Promise<number> {
  const curve = normalizeForwardCurve(curva);
  const response = await dashboardApi.getDashboardIndexesForward({
    curve: curve as any,
    initialDate: normalizeDate(dataInicial),
    finalDate: normalizeDate(dataFinal),
  });
  const data = unwrapData<AnyRecord>(response, "Forward da curva nao retornou dados.");
  const selectedField = normalizeOptionalField(campo, "taxaAnual");

  switch (normalizeKey(selectedField)) {
    case "taxaanual":
    case "anual":
      return requireNumber(data.forwardRateAnnual, "Forward anual nao retornado.");
    case "taxaperiodo":
    case "periodo":
      return requireNumber(data.forwardRatePeriod, "Forward do periodo nao retornado.");
    case "fator":
      return requireNumber(data.forwardFactor, "Fator forward nao retornado.");
    case "taxainicial":
      return requireNumber(data.initialRate, "Taxa inicial nao retornada.");
    case "taxafinal":
      return requireNumber(data.finalRate, "Taxa final nao retornada.");
    default:
      throw new Error("Campo invalido. Use taxaAnual, taxaPeriodo, fator, taxaInicial ou taxaFinal.");
  }
}

export async function bmfTerm(curva: string, dataInicial: string | number, dataFinal: string | number, campo?: string, dataReferencia?: string | number): Promise<number> {
  const response = await bmfCurvesApi.getBmfCurvesTermCalculate({
    curveId: normalizeTermCurve(curva) as any,
    startDate: normalizeDate(dataInicial),
    endDate: normalizeDate(dataFinal),
    annualBase: "Base252" as any,
    referenceDate: emptyDateToUndefined(dataReferencia),
  });
  const data = unwrapData<AnyRecord>(response, "Termo BMF nao retornou dados.");
  const selectedField = normalizeOptionalField(campo, "taxaPeriodo");

  switch (normalizeKey(selectedField)) {
    case "taxaperiodo":
    case "periodo":
      return requireNumber(data.periodRate, "Taxa do periodo nao retornada.");
    case "taxaanualizada":
    case "taxaanual":
    case "anual":
      return requireNumber(data.periodAnnualizedRate, "Taxa anualizada nao retornada.");
    case "taxainicial":
      return requireNumber(data.startAnnualRate, "Taxa inicial nao retornada.");
    case "taxafinal":
      return requireNumber(data.endAnnualRate, "Taxa final nao retornada.");
    default:
      throw new Error("Campo invalido. Use taxaPeriodo, taxaAnualizada, taxaInicial ou taxaFinal.");
  }
}

function unwrapData<T>(response: any, emptyMessage: string): T {
  if (response?.status === false) {
    throw new Error(response.message || emptyMessage);
  }
  const data = response?.data ?? response;
  if (data == null || (Array.isArray(data) && data.length === 0)) {
    throw new Error(response?.message || emptyMessage);
  }
  return data as T;
}

function selectInterpolatedRate(data: AnyRecord, campo: string): number {
  switch (normalizeKey(campo)) {
    case "taxaanual":
    case "anual":
      return requireNumber(data.annualRate, "Taxa anual nao retornada.");
    case "fatoranual":
      return requireNumber(data.annualFactor, "Fator anual nao retornado.");
    case "fatordiario":
    case "fator252":
      return requireNumber(data.dailyFactor252, "Fator diario 252 nao retornado.");
    default:
      throw new Error("Campo invalido. Use taxaAnual, fatorAnual ou fatorDiario.");
  }
}

function requireNumber(value: unknown, message: string): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  throw new Error(message);
}

function normalizeCurrencyCode(value: string): string {
  return String(value).trim().toUpperCase().replace("/BRL", "").replace("-BRL", "");
}

function normalizeCurrencyPair(value: string): string {
  const text = String(value).trim().toUpperCase().replace("/", "-");
  return text.includes("-") ? text : `${text}-BRL`;
}

function normalizeForwardCurve(value: string): string {
  const key = normalizeKey(value);
  const map: Record<string, string> = {
    cdi: "Di1",
    di: "Di1",
    di1: "Di1",
    pre: "Di1",
    ipca: "ImplicitIpca",
    ipcaimplicito: "ImplicitIpca",
    impliedipca: "ImplicitIpca",
    dap: "Dap",
    ddi: "Ddi",
    dolar: "Dol",
    dollar: "Dol",
    dol: "Dol",
    euro: "Eur",
    eur: "Eur",
  };
  return map[key] ?? value;
}

function normalizeTermCurve(value: string): string {
  const key = normalizeKey(value);
  const map: Record<string, string> = {
    cdi: "Di",
    di: "Di",
    ipca: "ImpliedIpca",
    ipcaimplicito: "ImpliedIpca",
    impliedipca: "ImpliedIpca",
    dap: "Dap",
    dolar: "Dollar",
    dollar: "Dollar",
    euro: "Euro",
  };
  return map[key] ?? value;
}

function emptyToUndefined(value?: string): string | undefined {
  const text = value == null ? "" : String(value).trim();
  return text ? text : undefined;
}

function emptyDateToUndefined(value?: string | number): string | undefined {
  if (value == null || String(value).trim() === "") return undefined;
  return normalizeDate(value);
}

export function normalizeOptionalField(value: unknown, defaultValue: string): string {
  const text = value == null ? "" : String(value).trim();
  return text ? text : defaultValue;
}

export function nextIsoDate(date: string): string {
  const normalized = normalizeDate(date);
  const next = new Date(`${normalized}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

function normalizeKey(value: string): string {
  return String(value)
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}
